import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, LogOut } from "lucide-react";
import { useClickOutside } from "../lib/hooks";

// The white, heavily shadowed container every page sits in.
export const CARD = "bg-white border border-slate-300 shadow-[0_35px_80px_-15px_rgba(15,23,42,0.55),0_15px_30px_-10px_rgba(15,23,42,0.30)]";

// Tailwind needs full class strings literally, so each accent is spelled out.
const LOGO = {
  green: { tile: "bg-green-600 shadow-green-200", text: "text-green-600" },
  amber: { tile: "bg-amber-500 shadow-amber-200", text: "text-amber-600" },
  red: { tile: "bg-red-600 shadow-red-200", text: "text-red-600" },
};

export function Logo({ accent = "green", label }) {
  const s = LOGO[accent] || LOGO.green;
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg ${s.tile}`}>N</div>
      <span className="font-black text-xl tracking-tighter uppercase">Need Entry<span className={s.text}> {label}</span></span>
    </div>
  );
}

export function PageNav({ accent = "green", label, user, onLogout, children }) {
  return (
    <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-xl border-b border-slate-200/50 z-50 px-10 py-5 flex justify-between items-center">
      <Logo accent={accent} label={label} />
      {children}
      <UserMenu user={user} onLogout={onLogout} accent={accent === "red" ? "red" : "green"} />
    </nav>
  );
}

export function HomeButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 pl-2 pr-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-200 hover:text-slate-800 transition-colors"
      title="Back to home"
    >
      <ChevronLeft size={14} strokeWidth={2.5} /> Home
    </button>
  );
}

export function UserMenu({ user, onLogout, accent = "green" }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, () => setOpen(false));

  const name = user?.name || user?.username || (user?.email ? user.email.split("@")[0] : "User");
  const dot = accent === "red" ? "bg-red-600" : "bg-slate-900";

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-3 p-1.5 pr-4 rounded-full bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all active:scale-95"
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs ${dot}`}>
          {(name?.[0] || "U").toUpperCase()}
        </div>
        <span className="hidden md:block text-[13px] font-semibold text-slate-700">{name}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl border border-slate-200/60 overflow-hidden z-[110]"
          >
            <div className="p-4 bg-slate-50/50 border-b border-slate-100">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1">Signed in as</p>
              <p className="text-sm font-bold text-slate-900 truncate">{user?.email || name}</p>
            </div>
            <div className="p-2">
              <button
                onClick={() => { setOpen(false); onLogout(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
